import { STAGE_MAP } from '../constants/rfpConstants';

/**
 * Determines the active step index (1-based) for the progress stepper
 * based on the RFP data and user role.
 */
export const getActiveStep = (data, isManager) => {
    if (!data) return 1;

    // 1. Check global_status.code (highest priority)
    const statusCode = data.global_status?.code;
    if (statusCode !== undefined && statusCode !== null) {
        const code = String(statusCode);
        if (!isManager) {
            // Member mapping (1-based index matching STEPS_MEMBER):
            // 0, 1 -> 1 (RFP Analyzed)
            // 2, 11 -> 2 (In Progress)
            // 3 -> 3 (Submitted to Lead)
            // 4, 5, 6, 7 -> 4 (RFP Finalized)
            if (['0', '1'].includes(code)) return 1;
            if (['2', '11'].includes(code)) return 2;
            if (['3'].includes(code)) return 3;
            if (['4', '5', '6', '7'].includes(code)) return 4;
        } else {
            if (STAGE_MAP[code]) return STAGE_MAP[code];
        }
    }

    // 2. Check global_status.label or other status labels
    const label = data.global_status?.label || data.PROPOSAL_STAGE || data.proposal_stage || data.status;
    if (label) {
        const upperLabel = String(label).toUpperCase();
        if (!isManager) {
            if (upperLabel.includes('ANALYZ') || upperLabel.includes('UPLOAD')) return 1;
            if (upperLabel.includes('PROGRESS') || upperLabel.includes('REFINEMENT')) return 2;
            if (upperLabel.includes('SUBMITTED_TO_TL') || upperLabel.includes('SUBMITTED TO LEAD') || upperLabel.includes('SUBMITTED TO TL')) return 3;
            if (upperLabel.includes('FINAL') || upperLabel.includes('SUBMITTED') || upperLabel.includes('ACCEPT') || upperLabel.includes('REJECT')) return 4;
        } else {
            if (STAGE_MAP[label]) return STAGE_MAP[label];
            if (STAGE_MAP[upperLabel]) return STAGE_MAP[upperLabel];
        }
    }

    // 3. Fallback to older status fields
    const status = data.status || 'Analysis';
    const s = String(status).toLowerCase();
    if (s.includes('analys')) return 1;
    if (s.includes('review')) return 2;
    if (s.includes('final')) return 3;
    if (s.includes('submit') || s.includes('complet')) return 4;

    return 1;
};

/**
 * Strips numeric prefix from text (e.g., "1. EXECUTIVE SUMMARY" -> "EXECUTIVE SUMMARY")
 */
export const stripPrefix = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.replace(/^\d+\.\s+/, '').trim();
};

/**
 * Formats API error details into a readable string
 */
export const formatErrorDetail = (detail) => {
    if (!detail) return 'An unexpected error occurred.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map(err => {
            if (typeof err === 'string') return err;
            if (typeof err === 'object') return `${err.msg || 'Error'} at ${err.loc?.join(' > ') || 'unknown'}`;
            return JSON.stringify(err);
        }).join(', ');
    }
    if (typeof detail === 'object') return JSON.stringify(detail);
    return String(detail);
};

/**
 * Detects if a section or content represents a query/question
 */
export const checkIsQuery = (sec, sub, content) => {
    const label = `${sec || ''} ${sub || ''}`.toLowerCase();
    const hasQueryWord = label.includes('query') || label.includes('question') || label.includes('clarification') || label.includes('assumption');
    if (hasQueryWord) return true;

    if (typeof content === 'string') {
        if (content.includes('\n> ') || content.startsWith('> ')) return true;
        const questionCount = content.split('\n').filter(l => l.trim().endsWith('?')).length;
        if (questionCount >= 2) return true;
    } else if (Array.isArray(content)) {
        const questionCount = content.filter(l => String(l).trim().endsWith('?')).length;
        if (questionCount >= 2) return true;
    }
    return false;
};

/**
 * Serializes data into a string format for editing
 */
export const serializeData = (data, type) => {
    if (!data) return '';
    if (type === 'table' && Array.isArray(data)) {
        if (data.length === 0) return '';
        const headers = Object.keys(data[0] || {});
        const headerRow = '| ' + headers.join(' | ') + ' |';
        const separatorRow = '| ' + headers.map(() => '---').join(' | ') + ' |';
        const bodyRows = data.map(row => '| ' + headers.map(h => String(row[h] ?? '')).join(' | ') + ' |');
        return [headerRow, separatorRow, ...bodyRows].join('\n');
    }

    let val = '';
    if (data && typeof data === 'object' && 'content' in data) {
        val = String(data.content || '').trim();
    } else if (typeof data === 'string') {
        val = data.trim();
    } else if (Array.isArray(data)) {
        val = data.map(item => `- ${typeof item === 'object' ? JSON.stringify(item) : item}`).join('\n');
    } else {
        val = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
    }
    return val;
};

/**
 * Deserializes edited string back into structured data
 */
export const deserializeData = (text, type) => {
    if (type === 'text') return text;
    if (type === 'list') {
        return text.split('\n')
            .map(line => line.replace(/^[*-]\s+/, '').trim())
            .filter(line => line.length > 0);
    }
    if (type === 'table') {
        const lines = text.trim().split('\n');
        if (lines.length < 2) return [];

        const headerLine = lines[0];
        const headers = headerLine.replace(/^\|/, '').replace(/\|$/, '').split('|')
            .map(h => h.trim())
            .filter(h => h.length > 0);

        const startIdx = (lines[1] && lines[1].includes('---')) ? 2 : 1;

        const rows = lines.slice(startIdx).map(line => {
            const cells = line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
            const obj = {};
            headers.forEach((h, i) => {
                obj[h] = cells[i] || '';
            });
            return obj;
        });
        return rows;
    }
    return text;
};

/**
 * Formats currency values with thousands separators and decimals
 */
export const formatCurrency = (amount, currency = 'AED') => {
    let targetCurrency = currency;
    if (!currency || currency === 'N/A' || currency === 'NOT' || !['AED', 'USD', 'IND', 'EUR', 'SAR'].includes(String(currency).toUpperCase())) {
        targetCurrency = 'AED';
    }

    if (amount === undefined || amount === null || amount === '') {
        return `${targetCurrency} 0`;
    }

    const cleanAmount = String(amount).replace(/[^0-9.-]+/g, "");
    const num = parseFloat(cleanAmount);
    if (isNaN(num)) {
        return `${targetCurrency} 0`;
    }

    // Determine the integer and decimal parts
    const parts = cleanAmount.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1] ? `.${parts[1].substring(0, 2)}` : '';

    let formattedInteger = '';
    if (targetCurrency === 'IND') {
        let lastThree = integerPart.substring(integerPart.length - 3);
        let otherDigits = integerPart.substring(0, integerPart.length - 3);
        if (otherDigits !== '') {
            lastThree = ',' + lastThree;
        }
        formattedInteger = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;
    } else {
        formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }

    const finalVal = formattedInteger + decimalPart;

    return `${targetCurrency} ${finalVal}`;
};

/**
 * Normalizes metadata keys between frontend and backend formats
 */
export const normalizeMetadata = (data, state = null) => {
    if (!data) return null;
    const normalized = { ...data };

    // Map nested metadata if it exists
    if (data.metadata) {
        if (!normalized.industry) normalized.industry = data.metadata.industry;
        if (!normalized.valuation) normalized.valuation = data.metadata.valuation;
        if (!normalized.currency) normalized.currency = data.metadata.currency;
        if (!normalized.proposal_deadline) normalized.proposal_deadline = data.metadata.proposal_deadline;
        if (!normalized.query_deadline) normalized.query_deadline = data.metadata.query_deadline;
        if (!normalized.template_id) normalized.template_id = data.metadata.template_id;
    }

    // Map global_status
    if (data.global_status) {
        normalized.PROPOSAL_STAGE = data.global_status.label;
        normalized.proposal_stage = data.global_status.label;
    }

    // Map estimated_budget
    if (data.estimated_budget) {
        normalized.valuation = data.estimated_budget;
        normalized.VALUATION = data.estimated_budget;
    }

    // Map sections array to top-level keys
    if (data.sections && Array.isArray(data.sections)) {
        if (!normalized.SECTION_STATUS) normalized.SECTION_STATUS = {};
        if (!normalized.SECTION_ASSIGNMENTS) normalized.SECTION_ASSIGNMENTS = {};

        data.sections.forEach(section => {
            const key = section.section_title || section.label || section.section_key || section.key;
            // Ensure ID is available for sidebar navigation
            const id = section.section_id || section.id || section._id;

            if (key) {
                // For internal consistency
                if (!section.label) section.label = key;
                if (!section.id) section.id = id;
                if (!section.key) section.key = section.section_key || key;

                normalized[key] = section.content || section.text || '';

                // Map status
                if (section.status) {
                    if (!normalized.SECTION_STATUS_CODE) normalized.SECTION_STATUS_CODE = {};
                    
                    if (typeof section.status === 'object') {
                        normalized.SECTION_STATUS[key] = section.status.label || section.status.name || section.status.text || 'In Progress';
                        normalized.SECTION_STATUS_CODE[key] = section.status.code;
                    } else {
                        normalized.SECTION_STATUS[key] = section.status;
                        // If it's a string, we might not have the code here, but the API usually sends objects
                    }
                }


                // Map assignments
                if (section.assigned_to) {
                    const assigned = Array.isArray(section.assigned_to) ? section.assigned_to : [section.assigned_to];
                    normalized.SECTION_ASSIGNMENTS[key] = assigned.map(member =>
                        typeof member === 'string' ? member : (member.name || `${member.first_name} ${member.last_name}`)
                    );
                }
            }
        });
    }

    const metadataFields = [
        'VALUATION', 'PROPOSAL_DEADLINE', 'QUERY_DEADLINE',
        'CLIENT_NAME', 'CLIENT_LOCATION', 'PROPOSAL_TITLE',
        'PROPOSAL_STAGE', 'CURRENCY', 'INDUSTRY', 'CONFIDENCE', 'REASONING'
    ];

    metadataFields.forEach(field => {
        const lowerField = field.toLowerCase();
        if (normalized[field] !== undefined) {
            normalized[lowerField] = normalized[field];
        } else if (normalized[lowerField] !== undefined) {
            normalized[field] = normalized[lowerField];
        }
    });

    if (normalized.CLIENT_LOCATION && !normalized.client_place) normalized.client_place = normalized.CLIENT_LOCATION;

    if (!normalized.SECTION_STATUS || typeof normalized.SECTION_STATUS !== 'object') {
        normalized.SECTION_STATUS = normalized.section_status || {};
    }

    if (!normalized.PROPOSAL_DEADLINE || normalized.PROPOSAL_DEADLINE === 'N/A') {
        normalized.PROPOSAL_DEADLINE = state?.PROPOSAL_DEADLINE || state?.proposal_deadline || normalized.proposal_deadline || normalized.extraction_data?.submission_deadline || '';
    }
    if (!normalized.VALUATION || normalized.VALUATION === 'N/A') {
        normalized.VALUATION = state?.VALUATION || state?.valuation || normalized.valuation || normalized.extraction_data?.valuation || '';
    }
    if (!normalized.QUERY_DEADLINE || normalized.QUERY_DEADLINE === 'N/A') {
        normalized.QUERY_DEADLINE = state?.QUERY_DEADLINE || state?.query_deadline || normalized.query_deadline || normalized.extraction_data?.query_deadline || '';
    }

    metadataFields.forEach(field => {
        normalized[field.toLowerCase()] = normalized[field];
    });

    const finalCurrency = (() => {
        const c = String(normalized.CURRENCY || normalized.currency || 'AED').toUpperCase();
        return ['AED', 'USD', 'IND', 'EUR', 'SAR'].includes(c) ? c : 'AED';
    })();
    normalized.currency = finalCurrency;
    normalized.CURRENCY = finalCurrency;

    return normalized;
};

/**
 * Formats inline markdown-like syntax to HTML
 */
export const formatInline = (text) => {
    if (!text) return '';
    let cleanContent = text;
    cleanContent = cleanContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    cleanContent = cleanContent.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    cleanContent = cleanContent.replace(/\[/g, '').replace(/\]/g, '');
    cleanContent = cleanContent.replace(/^#+\s*/g, '');
    return cleanContent.replace(/([\w.]+@[\w.]+\.\w+)/g, '<a href="mailto:$1" class="result-link">$1</a>');
};

/**
 * Extracts specific fields from potentially nested data
 */
export const extractField = (editableData, ...keys) => {
    if (!editableData) return null;
    for (const key of keys) {
        const val = editableData[key];
        if (val && typeof val === 'string' && val.toLowerCase() !== 'null') return val;
        for (const section of Object.values(editableData)) {
            if (typeof section === 'object' && section !== null && !Array.isArray(section)) {
                const sVal = section[key];
                if (sVal && typeof sVal === 'string' && sVal.toLowerCase() !== 'null') return sVal;
            }
        }
    }
    return null;
};

/**
 * Converts common HTML tags to Markdown
 */
export const htmlToMarkdown = (html) => {
    if (!html) return '';
    return html
        .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')
        .replace(/<b>(.*?)<\/b>/gi, '**$1**')
        .replace(/<em>(.*?)<\/em>/gi, '*$1*')
        .replace(/<i>(.*?)<\/i>/gi, '*$1*')
        .replace(/&nbsp;/g, ' ')
        .replace(/<br\s*\/?>/gi, '\n');
};

/**
 * Formats date from YYYY-MM-DD to DD/MM/YYYY
 */
export const formatDeadline = (dateStr) => {
    if (!dateStr) return 'N/A';
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
};

/**
 * Formats date for <input type="date"> (YYYY-MM-DD)
 */
export const formatForDateInput = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(dateStr)) {
        const separator = dateStr.includes('/') ? '/' : '-';
        const parts = dateStr.split(separator);
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    if (dateStr.includes('T')) return dateStr.split('T')[0];
    return dateStr;
};
